import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/seo/SEO';
import {
  getCityVerticalConfig,
  getCityVerticalPageContent,
  getMarketCityBySlug,
  getProgrammaticVerticalBySlug,
  getRelatedProgrammaticCityRoutes,
  isProgrammaticCityRouteEnabled,
} from '@/lib/programmaticData';

const FinancingCityPage: React.FC = () => {
  const { slug = '', city = '' } = useParams();
  const vertical = getProgrammaticVerticalBySlug(slug);
  const marketCity = getMarketCityBySlug(city);
  const cityVerticalConfig = vertical && marketCity ? getCityVerticalConfig(vertical.slug, marketCity.slug) : undefined;
  const cityPageContent = vertical && marketCity ? getCityVerticalPageContent(vertical.slug, marketCity.slug) : undefined;

  if (!vertical || !marketCity || !isProgrammaticCityRouteEnabled(slug, city)) {
    return (
      <div className="min-h-screen bg-[#f8f5ef] px-6 pb-24 pt-28 text-slate-900 md:pt-32">
        <div className="container mx-auto max-w-4xl rounded-3xl border border-slate-200 bg-white p-8">
          <h1 className="font-sans text-3xl text-slate-900">Page not found</h1>
          <p className="mt-3 text-slate-600">Try browsing available city and industry financing pages.</p>
          <Button asChild className="mt-6 rounded-full bg-slate-900 text-white hover:bg-slate-800">
            <Link to="/financing">Browse financing pages</Link>
          </Button>
        </div>
      </div>
    );
  }

  const appUrl = import.meta.env.VITE_APP_URL || '';
  const appSignUpUrl = appUrl ? `${appUrl}/auth?mode=signup` : '/auth?mode=signup';
  const monthly = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(vertical.monthlyExampleUsd);

  const bluf = `${vertical.industry} financing in ${marketCity.city}, ${marketCity.state} helps buyers move forward with monthly options while sellers get paid through Stripe checkout. Small businesses can quote quickly, share one link or QR code, and follow up based on real buyer activity instead of generic reminders.`;
  const nearbyRoutes = getRelatedProgrammaticCityRoutes(vertical.slug, marketCity.slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: `${vertical.industry} financing checkout`,
    areaServed: `${marketCity.city}, ${marketCity.state}`,
    provider: {
      '@type': 'Organization',
      name: 'LendLifted',
    },
    description: bluf,
  };

  return (
    <div className="min-h-screen bg-[#f8f5ef] pb-24 text-slate-900">
      <SEO
        title={`${vertical.industry} Financing in ${marketCity.city}, ${marketCity.state}`}
        description={bluf}
        canonical={`/financing/${vertical.slug}/${marketCity.slug}`}
        jsonLd={jsonLd}
      />

      <section className="px-6 pb-12 pt-28 md:pt-32">
        <div className="container mx-auto max-w-6xl rounded-[2rem] border border-slate-800 bg-slate-950 p-8 text-white md:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-200">{marketCity.city}, {marketCity.state}</p>
          <h1 className="mt-3 max-w-4xl font-sans text-3xl text-white md:text-5xl">
            {vertical.industry} financing for sellers in {marketCity.city}
          </h1>
          <p className="mt-5 max-w-4xl text-base text-slate-200 md:text-lg">{bluf}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-white px-6 text-slate-900 hover:bg-slate-100">
              <a href={appSignUpUrl}>
                Start selling
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-slate-600 bg-transparent text-slate-100 hover:bg-slate-800">
              <Link to={`/calculators/${vertical.slug}`}>Open payment calculator</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Local Market</p>
              <p className="mt-2 font-sans text-2xl text-slate-900">{marketCity.region}</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Example Monthly Option</p>
              <p className="mt-2 font-sans text-2xl text-slate-900">{monthly}/mo</p>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">Typical Sales Increase</p>
              <p className="mt-2 font-sans text-2xl text-slate-900">+{vertical.benchmarkLiftPercent}%</p>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <h2 className="font-sans text-2xl text-slate-900">What this usually improves</h2>
              <ul className="mt-4 space-y-2 text-sm text-slate-700">
                <li>Higher same-day closes when buyers are shown monthly options during decision moments.</li>
                <li>Fewer stalled quotes caused by upfront payment friction.</li>
                <li>Cleaner seller follow-up driven by real buyer activity.</li>
                <li>Local demand driver: {cityVerticalConfig?.localDemandDriver}</li>
              </ul>
            </CardContent>
          </Card>
          <Card className="rounded-3xl border border-slate-200 bg-white">
            <CardContent className="p-6">
              <h2 className="font-sans text-2xl text-slate-900">Frequently asked in {marketCity.city}</h2>
              <div className="mt-4 space-y-3 text-sm text-slate-700">
                <p><strong className="text-slate-900">Can independent sellers use this without Shopify?</strong><br />Yes. This is link and QR based, so it works for direct and in-person sales motions.</p>
                <p><strong className="text-slate-900">Do buyers apply in front of staff?</strong><br />No. Buyers can complete checkout on their own device from a private link flow.</p>
                <p><strong className="text-slate-900">What works best in this market?</strong><br />{cityVerticalConfig?.outreachTip}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="font-sans text-2xl text-slate-900">What buyers in {marketCity.city} most often finance</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {cityPageContent?.commonFinancedPurchases.map((purchase) => (
                <span
                  key={purchase}
                  className="rounded-full border border-slate-300 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  {purchase}
                </span>
              ))}
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Decision Window</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{cityPageContent?.localMetrics.typicalDecisionWindowDays} days</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Mobile Checkout Share</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{cityPageContent?.localMetrics.mobileCheckoutSharePercent}%</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-[0.14em] text-slate-500">Close Rate After Return Visit</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">{cityPageContent?.localMetrics.repeatVisitClosePercent}%</p>
              </div>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="font-sans text-2xl text-slate-900">Local playbook snapshot</h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-700">{cityPageContent?.localOpportunitySummary}</p>
            <div className="mt-4 space-y-3">
              {cityPageContent?.objectionHandling.map((item) => (
                <div key={item.objection} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <p className="text-sm font-semibold text-slate-900">{item.objection}</p>
                  <p className="mt-1 text-sm text-slate-700">{item.response}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="font-sans text-2xl text-slate-900">Launch checklist for this market</h2>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {cityPageContent?.launchChecklist.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ul>
            <h3 className="mt-6 font-sans text-xl text-slate-900">Messaging ideas that feel natural</h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {cityPageContent?.messagingAngles.map((angle) => (
                <span key={angle} className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700">
                  {angle}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto grid max-w-6xl gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="font-sans text-2xl text-slate-900">Local buying patterns</h2>
            <div className="mt-4 space-y-3 text-sm text-slate-700">
              <p><strong className="text-slate-900">How buyers usually decide:</strong> {marketCity.buyerBehavior}</p>
              <p><strong className="text-slate-900">Seasonality note:</strong> {marketCity.seasonalityNote}</p>
              <p><strong className="text-slate-900">Follow-up timing:</strong> {marketCity.leadWindowNote}</p>
              <p><strong className="text-slate-900">Best sales moment:</strong> {cityVerticalConfig?.localSalesMoment}</p>
            </div>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-white p-6">
            <h2 className="font-sans text-2xl text-slate-900">Recommended outreach channels</h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {cityVerticalConfig?.recommendedChannels.map((channel) => (
                <span
                  key={channel}
                  className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700"
                >
                  {channel}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-8">
        <div className="container mx-auto max-w-6xl rounded-3xl border border-slate-200 bg-white p-6">
          <h2 className="font-sans text-2xl text-slate-900">Related pages worth exploring</h2>
          <div className="mt-4 flex flex-wrap gap-2">
            {nearbyRoutes.map((route) => (
              <Link
                key={`${route.verticalSlug}-${route.citySlug}`}
                to={`/financing/${route.verticalSlug}/${route.citySlug}`}
                className="rounded-full border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:border-slate-900 hover:text-slate-900"
              >
                {(getProgrammaticVerticalBySlug(route.verticalSlug)?.industry || route.verticalSlug)} in {(getMarketCityBySlug(route.citySlug)?.city || route.citySlug)}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default FinancingCityPage;
